import { useState, useEffect } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Users, AlertTriangle, Shield, GraduationCap, FileText,
  MapPin, ChevronRight, Building2, X
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Coordenadas de Lomas Bayas (Antofagasta, Chile)
const LOMAS_BAYAS_CENTER = [-23.4167, -69.5000];
const DEFAULT_ZOOM = 14;

// Colores para marcadores
const MARKER_COLORS = {
  red: '#ef4444',
  blue: '#3b82f6',
  green: '#22c55e',
  orange: '#298082',
  purple: '#a855f7',
  yellow: '#eab308',
  cyan: '#06b6d4',
  pink: '#ec4899',
};

// Crear icono personalizado
const createCustomIcon = (color = '#f97316') => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        width: 32px;
        height: 32px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 2px 5px rgba(0,0,0,0.3);
      ">
        <div style="
          transform: rotate(45deg);
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 14px;
        ">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path></svg>
        </div>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

// Componente para centrar el mapa
function SetViewOnClick({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
}

// Panel de detalle del área
function AreaDetailPanel({ area, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (area) {
      fetchAreaData();
    }
  }, [area]);

  const fetchAreaData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/areas/${area.id}/resumen`);
      setData(res.data);
    } catch (error) {
      toast.error('Error al cargar datos del área');
    } finally {
      setLoading(false);
    }
  };

  if (!area) return null;

  return (
    <div className="absolute top-4 right-4 w-96 max-h-[calc(100vh-120px)] bg-white rounded-xl shadow-2xl z-[1000] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <Building2 className="w-5 h-5" />
          <h3 className="font-bold text-lg">{area.nombre}</h3>
        </div>
        <button onClick={onClose} className="text-white/80 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
          </div>
        ) : data ? (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-blue-50 rounded-lg p-3 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-xl font-bold text-blue-700">{data.stats.total_personas}</p>
                  <p className="text-xs text-blue-600">Personas</p>
                </div>
              </div>
              <div className="bg-red-50 rounded-lg p-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <div>
                  <p className="text-xl font-bold text-red-700">{data.stats.total_riesgos}</p>
                  <p className="text-xs text-red-600">Riesgos</p>
                </div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="text-xl font-bold text-emerald-700">{data.stats.total_controles}</p>
                  <p className="text-xs text-emerald-600">Controles</p>
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-purple-500" />
                <div>
                  <p className="text-xl font-bold text-purple-700">{data.stats.total_capacitaciones}</p>
                  <p className="text-xs text-purple-600">Capacitaciones</p>
                </div>
              </div>
            </div>

            {/* Riesgos */}
            {data.riesgos.length > 0 && (
              <div>
                <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  Riesgos ({data.riesgos.length})
                </h4>
                <div className="space-y-1">
                  {data.riesgos.slice(0, 5).map(r => (
                    <div key={r.id} className="text-sm bg-red-50 px-3 py-2 rounded-lg text-red-700">
                      {r.nombre}
                    </div>
                  ))}
                  {data.riesgos.length > 5 && (
                    <p className="text-xs text-slate-500 pl-3">+{data.riesgos.length - 5} más...</p>
                  )}
                </div>
              </div>
            )}

            {/* Controles */}
            {data.controles.length > 0 && (
              <div>
                <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-emerald-500" />
                  Controles ({data.controles.length})
                </h4>
                <div className="flex flex-wrap gap-1">
                  {data.controles.slice(0, 6).map(c => (
                    <span key={c.id} className="text-xs bg-emerald-50 px-2 py-1 rounded text-emerald-700">
                      {c.nombre}
                    </span>
                  ))}
                  {data.controles.length > 6 && (
                    <span className="text-xs text-slate-500">+{data.controles.length - 6} más</span>
                  )}
                </div>
              </div>
            )}

            {/* Capacitaciones */}
            {data.capacitaciones.length > 0 && (
              <div>
                <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-purple-500" />
                  Capacitaciones ({data.capacitaciones.length})
                </h4>
                <div className="space-y-1">
                  {data.capacitaciones.slice(0, 4).map(c => (
                    <div key={c.id} className="text-sm bg-purple-50 px-3 py-2 rounded-lg text-purple-700 flex justify-between">
                      <span>{c.nombre}</span>
                      <span className="text-xs text-purple-500">{c.horas}h</span>
                    </div>
                  ))}
                  {data.capacitaciones.length > 4 && (
                    <p className="text-xs text-slate-500 pl-3">+{data.capacitaciones.length - 4} más...</p>
                  )}
                </div>
              </div>
            )}

            {/* Personas */}
            {data.personas.length > 0 && (
              <div>
                <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-500" />
                  Personal ({data.personas.length})
                </h4>
                <div className="space-y-1">
                  {data.personas.slice(0, 5).map(p => (
                    <div key={p.id} className="text-sm flex items-center justify-between bg-slate-50 px-3 py-2 rounded-lg">
                      <span className="text-slate-700">{p.nombre} {p.apellido}</span>
                      <span className="text-xs text-slate-500">{p.cargo || 'Sin cargo'}</span>
                    </div>
                  ))}
                  {data.personas.length > 5 && (
                    <p className="text-xs text-slate-500 pl-3">+{data.personas.length - 5} más...</p>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-center text-slate-500 py-4">No hay datos disponibles</p>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-slate-200 p-3 bg-slate-50">
        <Link to={`/areas`}>
          <Button className="w-full bg-teal-500 hover:bg-teal-600">
            Ver detalle completo
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

export const MapaAreas = () => {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArea, setSelectedArea] = useState(null);
  const [mapCenter, setMapCenter] = useState(LOMAS_BAYAS_CENTER);

  useEffect(() => {
    fetchAreas();
  }, []);

  const fetchAreas = async () => {
    try {
      const res = await axios.get(`${API}/mapa/areas`);
      setAreas(res.data);
      
      // Si hay áreas con coordenadas, centrar en la primera
      if (res.data.length > 0 && res.data[0].latitud && res.data[0].longitud) {
        setMapCenter([res.data[0].latitud, res.data[0].longitud]);
      }
    } catch (error) {
      toast.error('Error al cargar áreas del mapa');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkerClick = (area) => {
    setSelectedArea(area);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-180px)] flex flex-col" data-testid="mapa-areas-page">
      {/* Header compacto */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">Mapa de Áreas - Lomas Bayas</h1>
          <p className="text-sm text-slate-500">Visualización geográfica de las áreas</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <MapPin className="w-4 h-4 text-teal-500" />
          <span>{areas.length} áreas georeferenciadas</span>
        </div>
      </div>

      {/* Map Container - ocupa todo el espacio disponible */}
      <div className="flex-1 rounded-xl overflow-hidden border border-slate-200 shadow-sm relative">
        {areas.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center bg-slate-50">
            <MapPin className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">Sin áreas georeferenciadas</h3>
            <p className="text-slate-500 text-center max-w-md mb-4">
              Para visualizar áreas en el mapa, edita cada área y agrega sus coordenadas (latitud y longitud).
            </p>
            <Link to="/areas">
              <Button className="bg-teal-500 hover:bg-teal-600">
                Ir a Gestión de Áreas
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <MapContainer
              center={mapCenter}
              zoom={DEFAULT_ZOOM}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
            >
              <LayersControl position="topright">
                <LayersControl.BaseLayer name="Mapa">
                  <TileLayer
                    attribution='&copy; OpenStreetMap'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer checked name="Satélite">
                  <TileLayer
                    attribution='&copy; Esri'
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Terreno">
                  <TileLayer
                    attribution='&copy; OpenTopoMap'
                    url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                  />
                </LayersControl.BaseLayer>
              </LayersControl>
              <SetViewOnClick center={mapCenter} />
              
              {areas.map(area => (
                <Marker
                  key={area.id}
                  position={[area.latitud, area.longitud]}
                  icon={createCustomIcon(area.color || MARKER_COLORS.orange)}
                  eventHandlers={{
                    click: () => handleMarkerClick(area),
                  }}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      <h3 className="font-bold text-slate-900 mb-2">{area.nombre}</h3>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-1">
                          <Users className="w-3 h-3 text-blue-500" />
                          <span>{area.stats?.personas || 0} personas</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3 text-red-500" />
                          <span>{area.stats?.riesgos || 0} riesgos</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Shield className="w-3 h-3 text-emerald-500" />
                          <span>{area.stats?.controles || 0} controles</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <GraduationCap className="w-3 h-3 text-purple-500" />
                          <span>{area.stats?.capacitaciones || 0} cursos</span>
                        </div>
                      </div>
                      <button 
                        onClick={() => handleMarkerClick(area)}
                        className="mt-3 w-full text-center text-sm bg-teal-500 text-white px-3 py-1.5 rounded hover:bg-teal-600 transition-colors"
                      >
                        Ver detalle completo
                      </button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>

            {/* Leyenda overlay sobre el mapa */}
            <div className="absolute bottom-3 left-3 z-[1000] bg-white/90 backdrop-blur-sm rounded-lg shadow-md border border-slate-200 px-3 py-2">
              <h4 className="text-[10px] font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Áreas</h4>
              <div className="space-y-1">
                {areas.map(area => (
                  <button
                    key={area.id}
                    onClick={() => {
                      setMapCenter([area.latitud, area.longitud]);
                      setSelectedArea(area);
                    }}
                    className="flex items-center gap-1.5 w-full text-left hover:bg-slate-100 rounded px-1 py-0.5 transition-colors"
                  >
                    <div 
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: area.color || MARKER_COLORS.orange }}
                    />
                    <span className="text-xs text-slate-700">{area.nombre}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
        
        {/* Detail Panel */}
        <AreaDetailPanel 
          area={selectedArea} 
          onClose={() => setSelectedArea(null)} 
        />
      </div>
    </div>
  );
};
